import {
  CFormCheck
} from '@coreui/react';
import { Button } from '@mui/material';
import { Toast } from 'primereact/toast';
import { useEffect, useState } from 'react';
import Layout2 from '../components/Layout2';
import Loader from '../components/Loader';
import { permissionNav } from '../components/NavigationPath';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../contexts/PermissionContext';

const modules = [
  { name: 'Users', value: 'users', is_crud: true, is_admin_permission: true },
  { name: 'Table/Floors', value: 'table-floor', is_crud: true, is_admin_permission: true },
  { name: 'Payment Methods', value: 'payment-methods', is_crud: true, is_admin_permission: true },
  { name: 'Products', value: 'products', is_crud: true, is_admin_permission: true },
  { name: 'Printers', value: 'printers', is_crud: true, is_admin_permission: true },
  { name: 'Clone Product', value: 'clone-product', is_crud: false, is_admin_permission: true },
  { name: 'Sort Category', value: 'sort-category', is_crud: false, is_admin_permission: true },
  { name: 'Reports', value: 'reports', is_crud: false, is_admin_permission: true },
  { name: 'Backup Printer', value: 'backup-printer', is_crud: false, is_admin_permission: true },
  { name: 'Audits', value: 'audits', is_crud: false, is_admin_permission: true },
  { name: 'Print Receipt', value: 'print-receipt', is_crud: false, is_admin_permission: false },
  { name: 'Re-Print Receipt', value: 're-print-receipt', is_crud: false, is_admin_permission: false },
  { name: 'Access POS', value: 'access-pos', is_crud: false, is_admin_permission: false },
  { name: 'Void Item', value: 'void-item', is_crud: false, is_admin_permission: false },
  { name: 'Payment Collection', value: 'payment-collection', is_crud: false, is_admin_permission: false },
  { name: 'CheckIn/CheckOut Access', value: 'checkin-checkout-access', is_crud: false, is_admin_permission: false },
  { name: 'Generate Frontend Report', value: 'generate-frontend-report', is_crud: false, is_admin_permission: false },
  { name: 'Reset Table', value: 'reset-table', is_crud: false, is_admin_permission: false },
  { name: 'Visible On Tablet', value: 'visible-on-tablet', is_crud: false, is_admin_permission: false },
  { name: 'Permission Page', value: 'permissions', is_crud: false, is_admin_permission: true },
  { name: 'Restaurants', value: 'restaurant', is_crud: false, is_admin_permission: true },
];

const PermissionsList = () => {
  const { getAllPermissions, updatePermissions } = usePermission();
  const { toast, restaurantId } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [groups, setGroups] = useState([]);

  const togglePermission = (groupIndex, moduleValue, action) => {
    setGroups((prev) =>
      prev.map((group, gIdx) =>
        gIdx === groupIndex
          ? {
            ...group,
            permissions: group.permissions.map((perm) => {
              if (perm.value !== moduleValue) return perm;

              if (action === "read" && perm.read) {
                return {
                  ...perm,
                  create: false,
                  read: false,
                  update: false,
                  delete: false,
                  access: false,
                };
              } else {
                return {
                  ...perm,
                  [action]: !perm[action],
                };
              }
            }),
          }
          : group
      )
    );
  };


  const handleSubmit = async () => {
    try {
      const payload = groups.map((group) => ({
        ...group,
        restaurant_id: restaurantId
      }));

      await updatePermissions(payload);
    } catch (error) {
      console.error("Error updating permissions", error.message);
    }
  };

  const fetchAllPermissions = async () => {
    setIsLoading(true);
    try {
      const data = await getAllPermissions();
      if (Array.isArray(data)) {
        const enforcedModule = 'visible-on-tablet';
        const patchedData = data.map((group) => {
          if (group.role === 'staff') {
            const updatedPermissions = group.permissions.map((perm) => {
              if (perm.value === enforcedModule) {
                return {
                  ...perm,
                  access: true,
                };
              }
              return perm;
            });
            return {
              ...group,
              permissions: updatedPermissions,
            };
          }
          return group;
        });

        setGroups(patchedData);
      }
    } catch (error) {
      console.error("Error fetching permissions", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (restaurantId) {
      fetchAllPermissions()
    }
  }, [restaurantId])

  return (
    <Layout2 items={permissionNav.items} navTitle={permissionNav.header}>
      <div className="d-flex flex-column w-100">
        <PageHeader title={'Permissions'} />
        {
          (!isLoading && groups.length === 0) ? (
            <div className="px-2 py-2 d-flex justify-content-center align-items-center mt-1" style={{ minHeight: '100px' }}>
              No permissions found for this restaurant.
            </div>
          ) : (
            <div
               style={{
                width: 'calc(100vw - 300px)',
              }} 
              className='px-4 py-2'>
              {isLoading ? (
                <Loader />
              ) : (
                <>
                <div
                  style={{
                    maxWidth: '100%',
                    maxHeight: 'calc(100vh - 150px)',
                    overflow: 'auto' 
                  }}>
                    <Toast ref={toast} />
                    <table className="permissions-table table align-middle text-center border-0 table-bordered" style={{ minWidth: `${200 + groups.length * 5 * 65}px` }}> 
                      <thead className="table-light">
                        <tr className="table-secondary">
                          <th>Module</th>
                          {groups.map((group) => (
                            <th key={group.id}>{group.group_name}</th>
                          ))}
                        </tr>
                        <tr>
                          <th>Actions</th>
                          {groups.map((group) => (
                            <th key={`${group.id}-actions`}>Create | Read | Update | Delete | Access</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {modules.map((module) => (
                          <tr key={module.value}>
                            <td className="text-start fw-semibold" style={{ minWidth: '200px' }}>{module.name}</td>
                            {groups.map((group, groupIndex) => {
                              const perm = group.permissions.find((p) => p.value === module.value);

                              return (
                                <td key={`${module.value}-${group.id}`}>
                                  <div className="d-flex justify-content-between px-2" style={{ minWidth: '300px' }}>
                                    {['create', 'read', 'update', 'delete', 'access'].map((action) => {
                                      const isStaff = group.role === 'staff';
                                      const isCRUDAction = ['create', 'read', 'update', 'delete'].includes(action);
                                      const isAccessAction = action === 'access';

                                      const shouldDisable =
                                        (isStaff && module.is_admin_permission) ||
                                        (!module.is_crud && isCRUDAction) ||
                                        (module.is_crud && isAccessAction) ||
                                        (isCRUDAction && action !== 'read' && !perm?.read) ||
                                        (isStaff && module.value === "visible-on-tablet" && action === 'access')
                                      // || (group.role === 'admin' && module.value === 'reset-table');

                                      return (
                                        <div key={`${module.value}-${group.id}-${action}`} style={{ width: '65px' }}>
                                          <CFormCheck
                                            title={action.toUpperCase()}
                                            checked={perm?.[action] || false}
                                            className={
                                              shouldDisable
                                                ? "form-check-input disabled-checkbox"
                                                : "form-check-input"
                                            }
                                            disabled={shouldDisable}
                                            onChange={() => togglePermission(groupIndex, module.value, action)}
                                            color="warning"
                                          />
                                        </div>
                                      );
                                    })}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="d-flex justify-content-end mt-4">
                    <Button variant="contained" color='primary' type="submit" className='ms-3' onClick={handleSubmit}>
                      Submit
                    </Button>
                  </div>
                </>
              )}
            </div>
          )
        }
      </div>
    </Layout2>
  );
};

export default PermissionsList;